import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPowerMeterData } from '../actions/powerMeterActions';
import './PowerMeterDashboard.css';

const PowerMeterDashboard = () => {
  const dispatch = useDispatch();
  const { loading, data, error } = useSelector((state) => state.powerMeter);
  
  // Get current system date
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [deviceId, setDeviceId] = useState('PM5560_001');

  useEffect(() => {
    dispatch(fetchPowerMeterData(selectedDate, deviceId));
  }, [dispatch, selectedDate, deviceId]);

  const handleRefresh = () => {
    dispatch(fetchPowerMeterData(selectedDate, deviceId));
  };

  // Optional: Show current date in UI
  const formatTime = (time) => {
    if (!time) return 'N/A';
    return time;
  };

  if (loading) return <div className="loading">Loading power meter data...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="dashboard-container">
      <h1>⚡ VoltTrace | Power Meter Dashboard</h1>
      
      <div className="controls">
        <div className="control-group">
          <label>Date : </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

        </div>
        
        <div className="control-group">
          <label>Device ID : </label>
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="Enter Device ID"
          />
        </div>
        
        <button onClick={handleRefresh} className="refresh-btn">
          Refresh Data
        </button>
      </div>

      {data.length === 0 ? (
        <p>No data available for {selectedDate}</p>
      ) : (
        <div className="table-responsive">
          <table className="voltage-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Device ID</th>
                <th>Date</th>
                <th>Time</th>
                <th>Voltage AB</th>
                <th>Voltage BC</th>
                <th>Voltage CA</th>
                <th>Voltage L-L Avg</th>
                <th>Voltage AN</th>
                <th>Voltage BN</th>
                <th>Voltage CN</th>
                <th>Voltage A-N Avg</th>
              </tr>
            </thead>
            <tbody>
              {data.map((record, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{record.DeviceID || 'N/A'}</td>
                  <td>{record.Ddate || selectedDate}</td>
                  <td>{formatTime(record.Dtime)}</td>
                  <td>{record.VoltageAB} V</td>
                  <td className={record.VoltageBC === '0.0' ? 'warning' : ''}>
                    {record.VoltageBC} V
                  </td>
                  <td>{record.VoltageCA} V</td>
                  <td>{record.VoltageLLAvg} V</td>
                  <td>{record.VoltageAN} V</td>
                  <td>{record.VoltageBN} V</td>
                  <td>{record.VoltageCN} V</td>
                  <td>{record.VoltageANAvg} V</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="summary">
        <h3>Summary Statistics</h3>
        {data.length > 0 && (
          <>
            <ul>
              <li><strong>Total Readings:</strong> {data.length}</li>
              <li><strong>Device:</strong> {data[0].DeviceID}</li>
              <li><strong>Selected Date:</strong> {selectedDate}</li>
              <li><strong>Data Date Range:</strong> {data[0].Ddate} - {data[data.length-1].Ddate}</li>
              <li><strong>Data Time Range:</strong> {data[0].Dtime} - {data[data.length-1].Dtime}</li>
              <li><strong>Avg Line-to-Line Voltage:</strong> 
                {(data.reduce((sum, r) => sum + parseFloat(r.VoltageLLAvg), 0) / data.length).toFixed(1)} V
              </li>
              <li><strong>Avg Line-to-Neutral Voltage:</strong> 
                {(data.reduce((sum, r) => sum + parseFloat(r.VoltageANAvg), 0) / data.length).toFixed(1)} V
              </li>
              <li className="warning-text">
                ⚠️ <strong>Note:</strong> Voltage BC is consistently 0.0V - Check Phase B-C connection
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default PowerMeterDashboard;